import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Navigate, Outlet } from "react-router";
import FullscreenLoading from "../fullscreen-loading";
import { useCheckUser } from "@/api/user/check-user";
import { useGetCrawlSession } from "@/api/knowledge-source/get-crawl-session";
import useGlobalLoading from "@/store/global-loading";
import { useEffect, useRef, useState } from "react";
import Topbar from "./topbar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./app-sidebar";
import SocketStatus from "./socket-status";
import { useMakeAgentOnline } from "@/api/agent-chat/useMakeAgentOnline";
import useOnboardingData from "@/store/onboarding-data";
import useSocketMessage from "@/store/socket-message";
import Cookies from "js-cookie";
import { useGetPermissions } from "@/api/permissions/get-permissions";
import useGlobalPermission from "@/store/user-permissions";

const AppLayout = () => {
    const { isAuthenticated, isLoading } = useKindeAuth();
    const { data: userData, isLoading: checkUserLoading } = useCheckUser();
    const { setIsLoading } = useGlobalLoading();
    const { storeData, userData: userStoreData } = useOnboardingData();
    const { data: userPermissions, isLoading: isUserPermissionsLoading, isError: isUserPermissionsError } = useGetPermissions();
    const { setPermissionList } = useGlobalPermission();
    const [isCheckingCrawl, setIsCheckingCrawl] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);

    // Check for ongoing crawl session on component mount
    const { data: crawlSessionData, isLoading: isCrawlSessionLoading, refetch: refetchCrawlSession } = useGetCrawlSession(storeData?.id || "");

    const { setMessages, addMessage, setConnectionStatus: setConnection } = useSocketMessage();

    useEffect(() => {
        if (storeData?.id) {
            setIsCheckingCrawl(true);
            refetchCrawlSession().finally(() => setIsCheckingCrawl(false));
        }
    }, [storeData?.id]); // Remove refetchCrawlSession from dependencies

    // If there's an ongoing crawl, connect to WebSocket
    useEffect(() => {
        if (
            crawlSessionData?.data?.status === "in_progress" &&
            !isCrawlSessionLoading &&
            !isCheckingCrawl &&
            !wsRef.current // Only connect if not already connected
        ) {
            connectToSocket();
        }
    }, [crawlSessionData, isCrawlSessionLoading, isCheckingCrawl, storeData?.id]);

    const connectToSocket = () => {
        if (!storeData?.id) return;

        // Close any existing connection first
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.close();
        }

        // Create WebSocket connection
        const ws = new WebSocket(`${import.meta.env.VITE_CRAWLER_BASE_URL}/${storeData.id}`);
        wsRef.current = ws;

        // Connection opened
        ws.onopen = () => {
            // Update socket messages store
            setMessages(["Resuming crawl process..."]);
            setConnection("Connected");
        };

        // Listen for messages
        ws.onmessage = (event) => {
            const data = event.data;
            if (data === "Crawling complete.") {
                setConnection("Disconnected");
                setMessages([]);
                ws.close();
                wsRef.current = null;
                // Refetch crawl session to update status
                refetchCrawlSession();
            } else {
                // Add new message to existing messages
                addMessage(data);
            }
        };

        // Handle errors
        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            setConnection("Error");
        };

        // Connection closed
        ws.onclose = () => {
            setConnection("Disconnected");
            wsRef.current = null;
        };

        // Return cleanup function
        return () => {
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
            wsRef.current = null;
        };
    };

    // Cleanup WebSocket connection on unmount
    useEffect(() => {
        return () => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.close();
            }
        };
    }, []);

    useEffect(() => {
        if (userPermissions?.data && !isUserPermissionsLoading && !isUserPermissionsError) {
            setPermissionList(userPermissions?.data);
        }
    }, [userPermissions, isUserPermissionsLoading, isUserPermissionsError, setPermissionList]);

    useMakeAgentOnline({
        storeId: storeData?.id,
        userId: userStoreData?.id,
        token: Cookies.get(import.meta.env.VITE_KINDE_ACCESS_TOKEN || "kinde_access_token") || "",
    });

    useEffect(() => {
        setIsLoading(isLoading || checkUserLoading);
    }, [isLoading, checkUserLoading, setIsLoading]);

    if (!isLoading && !isAuthenticated) {
        return <Navigate to="/auth/sign-in" replace />;
    }

    if (
        !checkUserLoading &&
        userData?.meta?.code === 404 &&
        userData?.meta?.message === "Store and User not found" &&
        userData?.data?.user?.role !== "member"
    ) {
        return <Navigate to="/onboarding/step-1" replace />;
        // navigate("/onboarding/step-1", { replace: true });
    }

    if (
        !checkUserLoading &&
        userData?.meta?.code === 403 &&
        userData?.meta?.message === "Store found but User not found" &&
        userData?.data?.user?.role !== "member"
    ) {
        return <Navigate to="/onboarding/step-2" replace />;
    }

    if (
        userData?.meta?.code === 200 &&
        userData?.meta?.message === "Store and User found" &&
        !userData?.data?.user?.is_paid &&
        userData?.data?.user?.role !== "member"
    ) {
        return <Navigate to="/onboarding/step-3" replace />;
    }

    return (
        <>
            <div>
                <Topbar />
                <SidebarProvider>
                    <AppSidebar />
                    <SidebarInset>
                        <main className="mt-10 min-h-[calc(100vh-4rem)] bg-gray-100 relative z-[2]">
                            <SidebarTrigger className="fixed left-36 top-3 z-20 size-10 rounded-none border bg-white lg:left-64 lg:top-5 lg:size-6" />
                            <div className="p-8">
                                <Outlet />
                            </div>
                            <SocketStatus />
                        </main>
                    </SidebarInset>
                </SidebarProvider>
            </div>

            <FullscreenLoading />
        </>
    );
};

export default AppLayout;
